import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { buildUrl } from '../api/client';
import { api } from '../api/endpoints';
import type { BrokerProfile } from '../api/types';
import { useDialog } from '../components/dialogContext';
import { ErrorMessage } from '../components/ErrorMessage';
import { MutationError } from '../components/MutationError';
import { shrinkImage } from '../expose/images';

type Key = Exclude<keyof BrokerProfile, 'has_photo' | 'photo_updated_at' | 'updated_at'>;

const groups: { title: string; fields: { key: Key; label: string; wide?: boolean; hint?: string }[] }[] = [
  {
    title: 'Kontakt im Exposé',
    fields: [
      { key: 'representative', label: 'Name (wie im Exposé)' },
      { key: 'position', label: 'Position', hint: 'z. B. Büroleiter Stuttgart' },
      { key: 'phone', label: 'Telefon' },
      { key: 'mobile', label: 'Mobil' },
      { key: 'whatsapp', label: 'WhatsApp', hint: 'leer = Mobilnummer' },
      { key: 'email', label: 'E-Mail' },
      { key: 'booking_url', label: 'Link zur Terminbuchung', hint: 'z. B. booking.maklerplan.com' },
      { key: 'website', label: 'Website' },
    ],
  },
  {
    title: 'Firma (Vertragspartei und Briefkopf)',
    fields: [
      { key: 'legal_name', label: 'Vertragspartner (rechtlicher Name)', wide: true, hint: 'Makler im Vertrag, z. B. Manfred Eisenhardt – leer = Firma / Standort' },
      { key: 'company_name', label: 'Firma / Standort (Marke)', wide: true, hint: 'Briefkopf und Exposé, z. B. Maklerpass · Standort Stuttgart' },
      { key: 'street', label: 'Straße und Hausnummer' },
      { key: 'postal_code', label: 'PLZ' },
      { key: 'city', label: 'Ort' },
      { key: 'vat_id', label: 'USt-IdNr.' },
      { key: 'register_info', label: 'Handelsregister', hint: 'z. B. Amtsgericht Stuttgart, HRB 12345' },
      { key: 'license_info', label: 'Erlaubnis nach § 34c GewO', hint: 'erteilt durch (Behörde), Datum' },
      { key: 'supervisory_authority', label: 'Aufsichtsbehörde' },
      { key: 'liability_insurance', label: 'Berufshaftpflicht (Versicherer)' },
      { key: 'commission_seller', label: 'Standardprovision Verkäufer (% inkl. USt.)', hint: 'Vorbelegung in Verträgen, z. B. 3,57' },
      { key: 'commission_buyer', label: 'Standardprovision Käufer (% inkl. USt.)' },
    ],
  },
];

// BrokerProfileSection: Maklerprofil mit Profilbild – Kontaktseite im Exposé (dynamisch je Partner) sowie
// Vertragspartei und Briefkopf in den Dokumenten. Ohne userId das eigene Profil, sonst (Benutzerverwaltung) das
// eines Partners.
export function BrokerProfileSection({ userId, title = 'Mein Maklerprofil (Exposé und Dokumente)' }: { userId?: string; title?: string }) {
  const key = ['broker-profile', userId ?? 'me'];
  const profile = useQuery({ queryKey: key, queryFn: ({ signal }) => (userId ? api.userBrokerProfile(userId, signal) : api.brokerProfile(signal)) });
  return (
    <section className="card" aria-labelledby={`broker-title-${userId ?? 'me'}`}>
      <h2 id={`broker-title-${userId ?? 'me'}`}>{title}</h2>
      <p className="muted">Foto und Kontakt erscheinen auf der Kontaktseite jedes Exposés dieses Partners; Firma und Anschrift als Vertragspartei in den Dokumenten.</p>
      {profile.isError && <ErrorMessage error={profile.error} onRetry={() => void profile.refetch()} />}
      {profile.data && (
        <>
          <Photo userId={userId} profile={profile.data} queryKey={key} />
          <BrokerForm key={profile.data.updated_at ?? 'neu'} userId={userId} initial={profile.data} queryKey={key} />
        </>
      )}
    </section>
  );
}

function Photo({ userId, profile, queryKey }: { userId?: string; profile: BrokerProfile; queryKey: string[] }) {
  const queryClient = useQueryClient();
  const dialog = useDialog();
  const src = profile.has_photo
    ? buildUrl(`${userId ? `/users/${userId}` : '/me'}/broker-profile/photo`, { v: profile.photo_updated_at ? Date.parse(profile.photo_updated_at) : 0 })
    : null;
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const blob = await shrinkImage(file, 900);
      return userId ? api.uploadUserBrokerPhoto(userId, blob) : api.uploadBrokerPhoto(blob);
    },
    onSuccess: (p) => queryClient.setQueryData(queryKey, p),
  });
  const remove = useMutation({
    mutationFn: () => (userId ? api.deleteUserBrokerPhoto(userId) : api.deleteBrokerPhoto()),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
  });
  return (
    <div className="broker-photo">
      {src ? <img src={src} alt="Profilbild" /> : <span className="broker-photo-empty">kein Foto</span>}
      <div className="form-actions">
        <label className="btn btn-small">
          {src ? 'Foto ersetzen' : 'Foto hochladen'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            aria-label="Profilbild auswählen"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) upload.mutate(file);
            }}
          />
        </label>
        {src && (
          <button
            type="button"
            className="btn btn-small btn-ghost"
            onClick={async () => {
              if (await dialog.confirm({ title: 'Profilbild entfernen?', message: 'Das Foto verschwindet aus allen Exposés.', confirmLabel: 'Entfernen', danger: true }))
                remove.mutate();
            }}
          >
            Entfernen
          </button>
        )}
        {upload.isPending && <span className="muted">Wird hochgeladen …</span>}
      </div>
      {(upload.error ?? remove.error) && <MutationError error={upload.error ?? remove.error} />}
    </div>
  );
}

function BrokerForm({ userId, initial, queryKey }: { userId?: string; initial: BrokerProfile; queryKey: string[] }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BrokerProfile>(initial);
  const save = useMutation({
    mutationFn: () => (userId ? api.saveUserBrokerProfile(userId, form) : api.saveBrokerProfile(form)),
    onSuccess: (p) => queryClient.setQueryData(queryKey, p),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <form onSubmit={submit} aria-label="Maklerprofil">
      {groups.map((g) => (
        <fieldset key={g.title} className="broker-group">
          <legend>{g.title}</legend>
          <div className="form-grid aligned">
            {g.fields.map((f) => (
              <label key={f.key} className={f.wide ? 'field wide' : 'field'}>
                <span>{f.label}</span>
                <input value={String(form[f.key] ?? '')} maxLength={300} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                {f.hint && <small className="muted">{f.hint}</small>}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
      {save.isError && <MutationError error={save.error} />}
      {save.isSuccess && <p className="alert alert-success">Gespeichert.</p>}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={save.isPending}>
          Speichern
        </button>
      </div>
    </form>
  );
}
